import React, { useState, useEffect } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckSquare, Trash2 } from 'lucide-react';

const TASK_TYPES = ['Follow Up', 'Call', 'Email', 'Meeting', 'Proposal', 'Review', 'Other'];
const PRIORITIES = ['high', 'medium', 'low'];
const STATUSES = ['open', 'in_progress', 'completed', 'cancelled'];

const CRMTaskModal = ({ open, task, companies, contacts, opportunities, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    task_type: 'Follow Up',
    priority: 'medium',
    status: 'open',
    due_date: '',
    reminder_date: '',
    company_id: '',
    contact_id: '',
    opportunity_id: '',
    assigned_to_id: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        task_type: task.task_type || 'Follow Up',
        priority: task.priority || 'medium',
        status: task.status || 'open',
        due_date: task.due_date ? task.due_date.split('T')[0] : '',
        reminder_date: task.reminder_date ? task.reminder_date.split('T')[0] : '',
        company_id: task.company_id || '',
        contact_id: task.contact_id || '',
        opportunity_id: task.opportunity_id || '',
        assigned_to_id: task.assigned_to_id || '',
      });
    } else {
      setFormData({
        title: '',
        description: '',
        task_type: 'Follow Up',
        priority: 'medium',
        status: 'open',
        due_date: '',
        reminder_date: '',
        company_id: '',
        contact_id: '',
        opportunity_id: '',
        assigned_to_id: '',
      });
    }
  }, [task, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Task title is required');
      return;
    }

    setLoading(true);
    try {
      const dataToSend = { ...formData };
      if (!dataToSend.company_id || dataToSend.company_id === '__none__') delete dataToSend.company_id;
      if (!dataToSend.contact_id || dataToSend.contact_id === '__none__') delete dataToSend.contact_id;
      if (!dataToSend.opportunity_id || dataToSend.opportunity_id === '__none__') delete dataToSend.opportunity_id;
      if (!dataToSend.due_date) delete dataToSend.due_date;
      if (!dataToSend.reminder_date) delete dataToSend.reminder_date;

      if (task?.id) {
        await api.crm.updateTask(task.id, dataToSend);
        toast.success('Task updated successfully');
      } else {
        await api.crm.createTask(dataToSend);
        toast.success('Task created successfully');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save task');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!task?.id) return;
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    setLoading(true);
    try {
      await api.crm.deleteTask(task.id);
      toast.success('Task deleted');
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to delete task');
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = formData.company_id && formData.company_id !== '__none__'
    ? contacts.filter(c => c.company_id === formData.company_id)
    : contacts;

  const filteredOpportunities = formData.company_id && formData.company_id !== '__none__'
    ? opportunities.filter(o => o.company_id === formData.company_id)
    : opportunities;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckSquare className="h-5 w-5 text-indigo-600" />
            {task ? 'Edit Task' : 'New Task'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Follow up with John about proposal"
                data-testid="task-title-input"
              />
            </div>

            <div>
              <Label>Task Type</Label>
              <Select
                value={formData.task_type}
                onValueChange={(value) => setFormData({ ...formData, task_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TASK_TYPES.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({ ...formData, priority: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map(p => (
                    <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => (
                    <SelectItem key={s} value={s}>{s.replace('_', ' ').charAt(0).toUpperCase() + s.replace('_', ' ').slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Due Date</Label>
              <Input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              />
            </div>

            <div>
              <Label>Company</Label>
              <Select
                value={formData.company_id || '__none__'}
                onValueChange={(value) => setFormData({ 
                  ...formData, 
                  company_id: value === '__none__' ? '' : value,
                  contact_id: '',
                  opportunity_id: ''
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No company</SelectItem>
                  {companies.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Contact</Label>
              <Select
                value={formData.contact_id || '__none__'}
                onValueChange={(value) => setFormData({ ...formData, contact_id: value === '__none__' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select contact" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No contact</SelectItem>
                  {filteredContacts.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Deal</Label>
              <Select
                value={formData.opportunity_id || '__none__'}
                onValueChange={(value) => setFormData({ ...formData, opportunity_id: value === '__none__' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select deal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No deal</SelectItem>
                  {filteredOpportunities.map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Reminder Date</Label>
              <Input
                type="date"
                value={formData.reminder_date}
                onChange={(e) => setFormData({ ...formData, reminder_date: e.target.value })}
              />
            </div>

            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Task details and notes..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="flex justify-between">
            <div>
              {task?.id && (
                <Button type="button" variant="destructive" onClick={handleDelete} disabled={loading}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={loading} data-testid="save-task-btn">
                {loading ? 'Saving...' : (task ? 'Update' : 'Create')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CRMTaskModal;
